import { useCallback } from "react";
import { toast } from "react-toastify";

export const useLandlordMetricsData = (
  filters,
  getWorkspaceMetrics,
  getWorkspaceMetricsFullInfo,
  getLandlordActivityMetrics,
  getLandlordFunnelMetrics,
  setSummaryMetrics,
  setLandlordMetrics,
  setTopLandlords,
  abortControllerRef,
  setLoadingSummary,
  setLoadingTable
) => {
  const aggregateByLandlord = useCallback((metrics) => {
    const grouped = {};

    metrics.forEach((metric) => {
      const landlordId = metric.landlord_id?._id;

      if (!landlordId) return;

      const key = landlordId;

      if (!grouped[key]) {
        grouped[key] = {
          landlord: metric.landlord_id,
          workspaces: new Map(),
          firstActivity: metric.created_at,
          lastActivity: metric.created_at,
          type: metric.landlord_type || 'N/A',
          metrics: [],
        };
      }

      grouped[key].metrics.push(metric);

      if (metric.workspaceId?._id && metric.workspaceId) {
        grouped[key].workspaces.set(metric.workspaceId._id, metric.workspaceId);
      }

      if (metric.created_at < grouped[key].firstActivity) {
        grouped[key].firstActivity = metric.created_at;
      }
      if (metric.created_at > grouped[key].lastActivity) {
        grouped[key].lastActivity = metric.created_at;
      }
    });

    return Object.values(grouped).map((group) => {
      const isItemDeleted = (item) => item.deleted === true || !item.tenant_id?.email && !item.tenant_id?.fname && !item.tenant_id?.lname;
      const hasTenantChecks = (item) => item.tenant_check === true || item.tenant_checks === true || (typeof item.tenant_checks === 'number' && item.tenant_checks > 0);

      const profiles = group.metrics.filter(f => !hasTenantChecks(f) && !isItemDeleted(f));
      const tenantChecks = group.metrics.filter(f => hasTenantChecks(f) && !isItemDeleted(f));
      const deletedProfiles = group.metrics.filter(f => !hasTenantChecks(f) && isItemDeleted(f));
      const deletedTenantChecks = group.metrics.filter(f => hasTenantChecks(f) && isItemDeleted(f));

      const workspaceArray = Array.from(group.workspaces.values());

      return {
        landlord: group.landlord,
        workspaceId: null,
        workspaceName: workspaceArray.length === 1
          ? workspaceArray[0]?.name || 'Unknown Workspace'
          : `${workspaceArray.length} workspaces`,
        workspaces: workspaceArray,
        tenantCount: profiles.length + deletedProfiles.length,
        activeTenantCount: profiles.length,
        deletedTenantCount: deletedProfiles.length,
        deletedTenantChecksCount: deletedTenantChecks.length,
        tenantChecks: tenantChecks.reduce((sum, item) => sum + (typeof item.tenant_checks === 'number' ? item.tenant_checks : 1), 0) + deletedTenantChecks.reduce((sum, item) => sum + (typeof item.tenant_checks === 'number' ? item.tenant_checks : 1), 0),
        firstActivity: group.firstActivity,
        lastActivity: group.lastActivity,
        type: group.type,
      };
    });
  }, []);

  const fetchSummaryMetrics = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoadingSummary(true);
    try {
      const [workspaceRes, activityRes, funnelRes] = await Promise.all([
        getWorkspaceMetrics({
          startDate: filters.startDate,
          endDate: filters.endDate,
          landlordId: filters.landlordId,
          workspaceId: "",
          tenantId: "",
          self_serving: filters.selfServing,
          landlord_type: filters.landlordType,
        }),
        getLandlordActivityMetrics({
          startDate: filters.startDate,
          endDate: filters.endDate,
          landlord_type: filters.landlordType,
        }),
        getLandlordFunnelMetrics({
          startDate: filters.startDate,
          endDate: filters.endDate,
          landlord_type: filters.landlordType,
        }),
      ]);

      if (
        workspaceRes?.status === "canceled" ||
        activityRes?.status === "canceled" ||
        funnelRes?.status === "canceled"
      ) {
        return;
      }

      let newSummary = {
        totalRegistrations: 0,
        totalTenants: 0,
        totalLandlords: 0,
        avgTenantsPerLandlord: 0,
        mostActiveWorkspace: "N/A",
        // Activity metrics
        avgWAU: 0,
        avgMAU: 0,
        totalTenantsInvited: 0,
        totalTenantsReviewed: 0,
        totalTenantChecksDone: 0,
        totalPropertiesImported: 0,
        totalPropertiesAwarded: 0,
        funnelProfiles: 0,
        funnelActivated: 0,
        funnelAvgTimeDays: 0,
        funnelAvgTimeMilliseconds: 0,
        funnelActivationRate: 0,
        weeklyBreakdown: [],
        monthlyBreakdown: []
      };

      if (workspaceRes && workspaceRes?.success && workspaceRes?.summary) {
        const summary = workspaceRes?.summary;
        const totalLandlordsCount = new Set(
          workspaceRes.data.map(
            (m) =>
              m.landlord_id?._id?.toString() || m.landlord_id?.toString()
          )
        ).size;

        // Find most active workspace
        const workspaceCounts = {};
        workspaceRes.data.forEach((m) => {
          const wsId =
            m.workspaceId?._id?.toString() || m.workspaceId?.toString();
          if (wsId) {
            workspaceCounts[wsId] = (workspaceCounts[wsId] || 0) + 1;
          }
        });
        const mostActiveWsId = Object.entries(workspaceCounts).sort(
          (a, b) => b[1] - a[1]
        )[0]?.[0];
        const mostActiveWorkspace =
          workspaceRes.data.find(
            (m) =>
              (m.workspaceId?._id?.toString() ||
                m.workspaceId?.toString()) === mostActiveWsId
          )?.workspaceId?.name || "N/A";

        newSummary = {
          ...newSummary,
          totalRegistrations: workspaceRes.data?.length || 0,
          totalTenants: summary.unique_tenants || 0,
          totalLandlords: totalLandlordsCount,
          avgTenantsPerLandlord:
            totalLandlordsCount > 0
              ? summary.unique_tenants / totalLandlordsCount
              : 0,
          mostActiveWorkspace,
        };
      }

      if (activityRes && activityRes.success && activityRes.data) {
        newSummary = {
          ...newSummary,
          ...activityRes.data
        };
      }

      if (funnelRes && funnelRes.success && funnelRes.data) {
        newSummary.funnelProfiles = funnelRes.data.landlords_profiles;
        newSummary.funnelActivated = funnelRes.data.landlords_activated;
        newSummary.funnelAvgTimeDays = funnelRes.data.avg_time_to_first_core_action_days;
        newSummary.funnelAvgTimeMilliseconds = funnelRes.data.avg_time_to_first_core_action_ms;
        newSummary.funnelActivationRate = funnelRes.data.activation_rate_percentage;
      }

      setSummaryMetrics(newSummary);
    } catch (err) {
      console.error("Error fetching summary metrics:", err);
      toast.error("Failed to fetch summary metrics");
    } finally {
      setLoadingSummary(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.landlordId,
    filters.landlordType,
    filters.selfServing,
    getWorkspaceMetrics,
    getLandlordActivityMetrics,
    getLandlordFunnelMetrics,
    setSummaryMetrics,
    setLoadingSummary,
  ]);

  const fetchLandlordMetrics = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoadingTable(true);
    try {
      const response = await getWorkspaceMetricsFullInfo(
        {
          startDate: filters.startDate,
          endDate: filters.endDate,
          landlordId: filters.landlordId,
          workspaceId: "",
          tenantId: "",
          self_serving: filters.selfServing,
          landlord_type: filters.landlordType,
          signal: controller.signal
        }
      );

      if (response?.status === "canceled") {
        return;
      }

      if (response && response.success && response.data) {
        const aggregated = aggregateByLandlord(response.data);

        // Sort by tenant count (descending)
        const sorted = aggregated.sort((a, b) => b.tenantCount - a.tenantCount);

        setLandlordMetrics(sorted);

        // Calculate top 5 landlords by tenant count
        const top5 = sorted?.slice(0, 5);
        setTopLandlords(top5);

      } else {
        setLandlordMetrics([]);
        setTopLandlords([]);
      }
    } catch (err) {
      console.error("Error fetching landlord metrics:", err);
      toast.error("Failed to fetch landlord metrics");
      setLandlordMetrics([]);
      setTopLandlords([]);
    } finally {
      setLoadingTable(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.landlordId,
    filters.landlordType,
    filters.selfServing,
    aggregateByLandlord,
    getWorkspaceMetricsFullInfo,
    setLandlordMetrics,
    setTopLandlords,
    setLoadingTable,
  ]);

  return {
    fetchSummaryMetrics,
    fetchLandlordMetrics,
  };
};